import { useState, useEffect, useRef, useCallback } from 'react'
import { SuccessModal, DeleteConfirmModal, ConnectionStatus } from '@/shared/ui'
import { useConnection } from '@/shared/context/ConnectionContext'
import { RegistrationModal } from './RegistrationModal'
import { ClassFilterChips } from './ClassFilterChips'
import { SchoolYearSelector } from './SchoolYearSelector'
import {
  TableLegend,
  DateRangeFilter,
  SlotTableRow
} from './volunteer-table'
import { useVolunteerSlots } from '../hooks/useVolunteerSlots'
import { useSlotFilters } from '../hooks/useSlotFilters'
import { useDayInfo } from '../hooks/useDayInfo'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { logger } from '@/utils/logger'
import { formatDayWithPrefix, removeClassPrefix, isCurrentOrFutureDate } from '@/utils/slotHelpers'
import { generateCalendarEventUrl } from '@/utils/calendarHelpers'
import { formatSchoolYearLabel } from '@/utils/schoolYearHelpers'
import type { VolunteerSlot, RegistrationData } from '../types/slot'

export function VolunteerTable() {
  const [schoolYear, setSchoolYear] = useState<string>('2026-2027')
  const { user, isAdmin, adminPermissions } = useAuth()
  const {
    slots,
    isLoading,
    loadSlots,
    lockSlot,
    unlockSlot,
    registerForSlot,
    cancelRegistration,
    canDeleteSlot,
    isSlotLocked,
    isSlotLockedByMe,
    getLockTimeRemaining
  } = useVolunteerSlots(schoolYear)

  const { isConnected: isRealtimeConnected, connectionUptime } = useConnection()

  const {
    selectedClasses,
    dateFilter,
    updateSelectedClasses,
    updateDateFilter,
    filterSlots
  } = useSlotFilters()

  const { getDayInfo } = useDayInfo(slots)

  useEffect(() => {
    logger.info('VolunteerTable component initialized', {
      component: 'VolunteerTable',
      user: user?.email || 'anonymous',
      data: { isAdmin, slotsCount: slots.length }
    })
  }, [user?.email, isAdmin, slots.length])

  const [selectedSlot, setSelectedSlot] = useState<VolunteerSlot | null>(null)
  const [showRegistrationModal, setShowRegistrationModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteSlotId, setDeleteSlotId] = useState<string | null>(null)
  const [lockCountdowns, setLockCountdowns] = useState<Record<string, number>>({})
  const [successRegistrationData, setSuccessRegistrationData] = useState<RegistrationData | null>(null)

  const currentDateRowRef = useRef<HTMLTableRowElement>(null)
  const selectedSlotRef = useRef<VolunteerSlot | null>(null)
  selectedSlotRef.current = selectedSlot

  useEffect(() => {
    if (!isLoading && slots.length > 0 && currentDateRowRef.current) {
      setTimeout(() => {
        currentDateRowRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }, 300)
    }
  }, [isLoading, slots.length])

  useEffect(() => {
    const interval = setInterval(() => {
      const newCountdowns: Record<string, number> = {}
      
      slots.forEach(slot => {
        if (isSlotLocked(slot)) {
          const remaining = getLockTimeRemaining(slot)
          if (remaining > 0) {
            newCountdowns[slot.id] = remaining
          }
        }
      })
      
      setLockCountdowns(newCountdowns)
    }, 1000)

    return () => clearInterval(interval)
  }, [slots, isSlotLocked, getLockTimeRemaining])

  const handleRegister = async (slotId: string) => {
    if (!user?.email) {
      setSuccessMessage('שגיאה: משתמש לא מחובר')
      setTimeout(() => setSuccessMessage(''), 4000)
      return
    }

    const slot = slots.find(s => s.id === slotId)
    if (slot && !isCurrentOrFutureDate(slot.date)) {
      setSuccessMessage('לא ניתן להירשם לתאריך בעבר')
      setTimeout(() => setSuccessMessage(''), 4000)
      return
    }

    const lockResult = await lockSlot(slotId)

    if (!lockResult.success) {
      setSuccessMessage(`שגיאה בנעילת התורנות: ${lockResult.error}`)
      setTimeout(() => setSuccessMessage(''), 4000)
      return
    }

    const slotForModal = slots.find(s => s.id === slotId)
    if (slotForModal) {
      const expiresAt = lockResult.data?.expires_at || new Date(Date.now() + 60000).toISOString()
      const lockedSlot: VolunteerSlot = {
        ...slotForModal,
        locked_by: user.email,
        locked_at: new Date().toISOString(),
        lock_expires_at: expiresAt
      }
      setSelectedSlot(lockedSlot)
      setShowRegistrationModal(true)
    }
  }

  const handleAddToCalendar = (slot: VolunteerSlot) => {
    const calendarUrl = generateCalendarEventUrl(slot)
    window.open(calendarUrl, '_blank')
  }

  const handleRegistrationComplete = async (
    userName: string,
    email: string,
    phoneNumber: string,
    selectedClass?: string,
    siblingClasses?: string[]
  ) => {
    if (!selectedSlot || !user?.email) return

    try {
      const result = await registerForSlot(selectedSlot.id, userName, phoneNumber, email, siblingClasses)

      if (result.success) {
        setShowRegistrationModal(false)
        setSelectedSlot(null)

        setSuccessRegistrationData({
          volunteerName: userName,
          date: selectedSlot.displayDate || selectedSlot.date,
          day: formatDayWithPrefix(selectedSlot.day),
          className: selectedClass || selectedSlot.class_name,
          time: selectedSlot.time_slot || '07:30-08:10'
        })

        setSuccessMessage('נרשמת בהצלחה לתורנות!')
        setShowSuccessModal(true)

        await loadSlots()
        setTimeout(() => setSuccessMessage(''), 4000)
      } else {
        setShowRegistrationModal(false)
        setSelectedSlot(null)
        setSuccessMessage(`שגיאה ברישום: ${result.error}`)
        setTimeout(() => setSuccessMessage(''), 4000)
      }
    } catch (error) {
      setShowRegistrationModal(false)
      setSelectedSlot(null)
      setSuccessMessage('שגיאה ברישום לתורנות')
      setTimeout(() => setSuccessMessage(''), 4000)
    }
  }

  const handleDeleteRequest = (slotId: string) => {
    const slot = slots.find(s => s.id === slotId)
    if (!slot || !canDeleteSlot(slot)) {
      setSuccessMessage('לא ניתן למחוק תורנות זו')
      setTimeout(() => setSuccessMessage(''), 4000)
      return
    }
    
    setDeleteSlotId(slotId)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteSlotId) return

    const slot = slots.find(s => s.id === deleteSlotId)
    const isMyRegistration = slot?.volunteer_email === user?.email

    try {
      const result = await cancelRegistration(deleteSlotId)
      
      if (result.success) {
        const message = isMyRegistration ? 'התורנות בוטלה בהצלחה' : 'התורנות נמחקה בהצלחה'
        setSuccessMessage(message)
        await loadSlots()
      } else {
        const errorMsg = isMyRegistration ? `שגיאה בביטול: ${result.error}` : `שגיאה במחיקה: ${result.error}`
        setSuccessMessage(errorMsg)
      }
    } catch (error) {
      const errorMsg = isMyRegistration ? 'שגיאה בביטול התורנות' : 'שגיאה במחיקת התורנות'
      setSuccessMessage(errorMsg)
    } finally {
      setShowDeleteConfirm(false)
      setDeleteSlotId(null)
      setTimeout(() => setSuccessMessage(''), 4000)
    }
  }

  const handleModalClose = useCallback(async () => {
    const currentSlot = selectedSlotRef.current
    if (currentSlot) {
      await unlockSlot(currentSlot.id)
    }
    setShowRegistrationModal(false)
    setSelectedSlot(null)
  }, [unlockSlot])

  const getSlotStatus = (slot: VolunteerSlot) => {
    if (isSlotLocked(slot)) {
      const remaining = lockCountdowns[slot.id] !== undefined
        ? lockCountdowns[slot.id]
        : getLockTimeRemaining(slot)
      const isMyLock = isSlotLockedByMe(slot)
      return {
        isLocked: true,
        isMyLock,
        timeRemaining: remaining,
        lockedBy: slot.locked_by
      }
    }

    return {
      isLocked: false,
      isMyLock: false,
      timeRemaining: 0,
      lockedBy: null
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="mr-3 text-gray-600">טוען תורניות...</span>
      </div>
    )
  }

  return (
    <div className="w-full" dir="rtl">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-900 font-hebrew">
              לוח תורנויות
            </h2>
            <SchoolYearSelector
              currentSchoolYear={schoolYear}
              onYearChange={setSchoolYear}
            />
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-100 to-amber-100 border border-orange-300 rounded-full">
                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold text-orange-800 font-hebrew">
                  מצב אדמין פעיל
                </span>
              </div>
            )}
            <ConnectionStatus
              isConnected={isRealtimeConnected}
              connectionUptime={connectionUptime}
            />
          </div>
        </div>
        
        <div className="mb-6">
          <ClassFilterChips
            slots={slots}
            selectedClasses={selectedClasses}
            onSelectionChange={updateSelectedClasses}
          />
        </div>

        <DateRangeFilter
          currentFilter={dateFilter}
          onFilterChange={updateDateFilter}
          className="mb-6"
        />

      </div>

      {successMessage && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800 px-6 py-4 rounded-2xl shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      <TableLegend />

      {!isLoading && (
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 px-6 py-4 border-b border-gray-200">
            <p className="text-center text-sm text-gray-700 font-hebrew font-semibold">
              📅 מציג {filterSlots(slots).length} ימי תורנות
              {selectedClasses.length > 0 && (
                <span className="text-primary font-bold">
                  {selectedClasses.length === 1
                    ? ` - מסונן לכיתה ${removeClassPrefix(selectedClasses[0])}`
                    : ` - מסונן ל-${selectedClasses.length} כיתות`
                  }
                </span>
              )}
              {dateFilter !== 'all' && (
                <span className="text-blue-600 font-bold">
                  {dateFilter === 'week' && ' - השבוע הקרוב'}
                  {dateFilter === 'month' && ' - החודש הנוכחי'}
                  {dateFilter === 'future' && ' - עתיד בלבד'}
                </span>
              )}
              | שנת הלימודים {formatSchoolYearLabel(schoolYear)}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="volunteer-table w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-right font-semibold text-gray-900 font-hebrew">
                    תאריך ויום
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-900 font-hebrew">
                    כיתה
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-900 font-hebrew">
                    שם הורה
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-900 font-hebrew hidden sm:table-cell">
                    מייל
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 font-hebrew">
                    פעולה
                  </th>
                </tr>
              </thead>
              <tbody>
                {filterSlots(slots).map((slot, index, filteredSlots) => {
                  const status = getSlotStatus(slot)
                  const canDelete = canDeleteSlot(slot)
                  const isFirstFutureDate = index === filteredSlots.findIndex(s => isCurrentOrFutureDate(s.date))

                  return (
                    <SlotTableRow
                      key={slot.id}
                      ref={isFirstFutureDate ? currentDateRowRef : null}
                      slot={slot}
                      index={index}
                      isFirstFutureDate={isFirstFutureDate}
                      status={status}
                      canDelete={canDelete}
                      userEmail={user?.email || null}
                      getDayInfo={getDayInfo}
                      onRegister={handleRegister}
                      onDelete={handleDeleteRequest}
                      onAddToCalendar={handleAddToCalendar}
                    />
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showRegistrationModal && selectedSlot && (
        <RegistrationModal
          slot={selectedSlot}
          onSubmit={handleRegistrationComplete}
          onClose={handleModalClose}
        />
      )}

      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          registrationData={successRegistrationData}
        />
      )}

      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
